import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useLenis from "../hooks/useLenis";
import useSeo from "../hooks/useSeo";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../lib/reveal";
import { getResource, resources } from "../data/resources";
import { formatDate } from "../data/resources";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function ResourceArticle() {
  useLenis();
  const { slug } = useParams();
  const { t } = useLang();
  const article = getResource(slug);

  useSeo(
    article
      ? {
          title: `${article.title} | Fresh Earth Resource`,
          description: article.dek,
          path: `/resources/${slug}`,
          image: article.image,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: article.title,
            description: article.dek,
            image: [article.image],
            articleSection: article.category,
            publisher: { "@type": "Organization", name: "Fresh Earth" },
          },
        }
      : { title: "Note not found · Fresh Earth", path: `/resources/${slug}` }
  );

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) {
    return (
      <div className="bg-bone text-ink min-h-screen flex flex-col items-center justify-center">
        <p className="font-serif text-3xl">Note not found.</p>
        <Link to="/resources" className="mt-4 underline text-moss">Back to Resource Hub</Link>
      </div>
    );
  }

  const idx = resources.findIndex((r) => r.slug === slug);
  const next = resources[(idx + 1) % resources.length];

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="resource-article-page">
      <Navbar />
      <main className="pt-32 md:pt-40">
        <article className="max-w-[820px] mx-auto px-6 md:px-12">
          <Link to="/resources" data-testid="resource-back" className="inline-flex items-center gap-2 text-sm tracking-[0.08em] uppercase text-slate hover:text-ink transition-colors duration-300">
            <ArrowLeft size={16} /> {t("Resource Hub", "संसाधन केंद्र")}
          </Link>

          <Reveal className="mt-8">
            <div className="text-xs tracking-[0.14em] uppercase text-terracotta font-semibold">{article.category} · {article.readTime} · {formatDate(article.date)}</div>
          </Reveal>
          <Reveal>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.02] mt-4">{t(article.title, article.hi?.title)}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-xl text-slate leading-relaxed">{t(article.dek, article.hi?.dek)}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl overflow-hidden aspect-[16/9] border border-stone/20 mt-10">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <div className="mt-14 space-y-10">
            {article.body.map((s) => (
              <Reveal key={s.h}>
                <h2 className="font-serif text-2xl md:text-3xl text-ink mb-3">{s.h}</h2>
                <p className="text-lg leading-relaxed text-slate">{s.p}</p>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="my-16 bg-forest text-bone rounded-3xl p-10 grain relative overflow-hidden">
              <p className="font-serif text-2xl md:text-3xl leading-snug relative">{t("Want this outcome on your site?", "अपनी साइट पर यह परिणाम चाहते हैं?")}</p>
              <Link to="/#contact" className="inline-flex mt-6 items-center rounded-full bg-bone text-forest text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300 relative">
                {t("Request a Pilot", "पायलट का अनुरोध करें")}
              </Link>
            </div>
          </Reveal>
        </article>

        <div className="border-t border-stone/20">
          <div className="max-w-[820px] mx-auto px-6 md:px-12 py-12">
            <Link to={`/resources/${next.slug}`} data-testid="resource-next" className="group block">
              <div className="text-xs tracking-[0.14em] uppercase text-stone">{t("Next note", "अगली टिप्पणी")}</div>
              <div className="font-serif text-2xl md:text-3xl mt-2 text-ink group-hover:text-moss transition-colors duration-300 flex items-center gap-3">
                {t(next.title, next.hi?.title)} <ArrowRight size={22} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
